import { esc } from "./utils.js";

/**
 * The section switcher used by the cohort and dive-site detail pages.
 *
 * `tabs` is [{ id, label, count }]. Counts are parenthesised so they read
 * as a quantity rather than as part of the label, and muted so the label
 * stays the thing you read.
 *
 * Two controls are rendered and CSS picks one. Measured on the cohort
 * page: the labels start wrapping below 360px of container width, and
 * the row as a whole — tabs plus the action button — needs 418px. Every
 * phone in portrait is under that, so below Bootstrap's `sm` the tabs
 * become a native <select>.
 *
 * A native select rather than a styled dropdown: it gets the platform
 * picker on iOS and Android, it is keyboard and screen-reader accessible
 * without any work, and it needs no JS beyond a change handler.
 *
 * Rendering both and hiding one with `d-none`/`d-sm-none` means no
 * resize listener and no layout measurement at runtime — the hidden
 * control is display:none, so it is not focusable or announced.
 *
 * Returns markup only; the caller binds via attachTabSelector, because
 * each page decides what a switch does.
 */
export function tabSelectorHtml(tabs, activeId, { id = "section-tabs", label = "Section" } = {}) {
  const items = tabs
    .map((tab) => {
      const on = tab.id === activeId;
      // A count is optional. Detail pages have three tabs holding
      // different things and the numbers tell them apart; a pair of tabs
      // over one collection — List and Map — would just print the same
      // number twice.
      const count =
        tab.count == null ? "" : `<span class="text-slate small ms-1">(${tab.count})</span>`;
      return `<li class="nav-item">
        <button class="nav-link${on ? " active" : ""}" data-tab="${esc(tab.id)}"
                role="tab" aria-selected="${on}">${esc(tab.label)}${count}</button>
      </li>`;
    })
    .join("");

  const options = tabs
    .map(
      (tab) =>
        `<option value="${esc(tab.id)}"${tab.id === activeId ? " selected" : ""}>${
          tab.count == null ? esc(tab.label) : `${esc(tab.label)} (${tab.count})`
        }</option>`
    )
    .join("");

  return `
    <ul class="nav dc-tab-bar mb-0 d-none d-sm-flex" id="${esc(id)}" role="tablist">${items}</ul>
    <select class="form-select form-select-sm d-sm-none" style="width:auto;"
            id="${esc(id)}-select" data-tab-select aria-label="${esc(label)}">${options}</select>`;
}

/** Binds both controls. `onSelect` is called with the chosen tab id. */
export function attachTabSelector(host, onSelect) {
  host.querySelectorAll(".nav-link[data-tab]").forEach((button) => {
    button.addEventListener("click", () => onSelect(button.dataset.tab));
  });
  const select = host.querySelector("[data-tab-select]");
  if (select) select.addEventListener("change", () => onSelect(select.value));
}
